import { z } from 'zod';

/**
 * Environment variables schema
 * Validates all required env vars at build time
 */
const envSchema = z.object({
  VITE_API_URL: z
    .string()
    .default('/api/v1')
    .describe('Backend API URL'),

  VITE_GOOGLE_CLIENT_ID: z
    .string()
    .optional()
    .describe('Google OAuth Client ID'),

  VITE_GEOAPIFY_API_KEY: z
    .string()
    .optional()
    .describe('Geoapify API Key for Address Autocomplete'),

  VITE_ENVIRONMENT: z
    .enum(['development', 'staging', 'production'])
    .default('development')
    .describe('Current environment'),

  VITE_APP_VERSION: z
    .string()
    .default('local')
    .describe('App version (commit SHA or tag)'),
});

/**
 * Parse and validate environment variables
 */
function parseEnv() {
  const parsed = envSchema.safeParse({
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    VITE_GEOAPIFY_API_KEY: import.meta.env.VITE_GEOAPIFY_API_KEY,
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
    VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
  });

  if (!parsed.success) {
    // In development, show detailed error
    if (import.meta.env.DEV) {
      throw new Error(
        `Invalid environment variables:\n${JSON.stringify(
          parsed.error.flatten().fieldErrors,
          null,
          2
        )}`
      );
    }

    // In production, use defaults silently
    return envSchema.parse({});
  }

  return parsed.data;
}

/**
 * Get API URL - automatically detect if accessing from network IP
 * In production, always use relative /api/v1 (same-origin via ALB)
 * In development on localhost, use relative /api/v1 (Vite proxy)
 * In development on network IP, use http://hostname:3000/api/v1
 */
function getApiUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && envUrl !== '/api/v1') {
    return envUrl;
  }

  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `http://${hostname}:3000/api/v1`;
    }
  }

  return '/api/v1';
}

/**
 * Validated environment variables
 * Use this instead of import.meta.env directly
 */
const parsedEnv = parseEnv();
export const env = {
  ...parsedEnv,
  VITE_API_URL: getApiUrl(),
};

/**
 * Type for validated env
 */
export type Env = z.infer<typeof envSchema>;

/**
 * Check if running in production
 */
export const isProduction = env.VITE_ENVIRONMENT === 'production';

/**
 * Check if running in development
 */
export const isDevelopment = env.VITE_ENVIRONMENT === 'development';

